import useCapabilities, { useHasRoles } from "@/hooks/useCapabilities";
import useCurrentUser from "@/hooks/useCurrentUser";
import { isPgaSectionWorkdayDepartment } from "@/pages/tasks/(tabbed)/preferences/_isPgaSectionWorkdayDepartment";
import { ROLE } from "@/providers/AuthorizationProvider/types";

/**
 * True only for HQ staff: capabilities loaded, staff runtime (no portal roles),
 * no section roles, PGA Corporation organization, and a non-section department.
 */
// Inference beats ROLE.HQ_STAFF: that role is auto-assigned too broadly to prove HQ employment.
export default function useIsHqStaff(): boolean {
  const { loaded } = useCapabilities();
  const isPortalStaff = useHasRoles([ROLE.UNIVERSITY_STAFF, ROLE.PGA_MAGAZINE_STAFF]);
  const isSectionStaff = useHasRoles([ROLE.SECTION_STAFF, ROLE.SECTION_ADMIN]);
  const { user } = useCurrentUser();

  if (!loaded) return false;
  if (isPortalStaff || isSectionStaff) return false;
  if (user?.organization !== "PGA Corporation") return false;
  const department = user?.department ?? "";
  if (!department.trim()) return false;
  return !isPgaSectionWorkdayDepartment(department);
}

/**
 * Interaction Analytics access: HQ staff plus global admins. Global admins
 * bypass the HQ employment checks entirely (superuser semantic, matching other
 * global-admin-gated surfaces) but still wait for capabilities to load.
 *
 * Product scoping, not a security boundary: the Backoffice analytics queries
 * authorize on dashboard capabilities (viewPeopleInteractions covers section
 * staff too), so this gate only controls who sees the dashboard UI. Per-call
 * topic, link, and recording fields stay privacy-gated server-side.
 */
export function useCanAccessInteractionAnalytics(): boolean {
  const { loaded } = useCapabilities();
  const isGlobalAdmin = useHasRoles([ROLE.GLOBAL_ADMIN]);
  const isHqStaff = useIsHqStaff();

  if (!loaded) return false;
  return isGlobalAdmin || isHqStaff;
}
